import { cn } from "@repo/design-system/lib/utils";
import {
	type ComponentProps,
	cloneElement,
	isValidElement,
	type ReactElement,
} from "react";

type ButtonVariant =
	| "default"
	| "destructive"
	| "outline"
	| "secondary"
	| "ghost"
	| "link";
type ButtonSize = "default" | "sm" | "lg" | "icon";

const base =
	"inline-flex items-center justify-center gap-2 whitespace-nowrap rounded-lg font-semibold text-sm outline-brand transition duration-100 focus-visible:outline-2 focus-visible:outline-offset-2 disabled:pointer-events-none disabled:opacity-50 [&_svg]:pointer-events-none [&_svg]:size-4 [&_svg]:shrink-0";

// shadcn variants mapped to Untitled UI button styling.
const variantStyles: Record<ButtonVariant, string> = {
	default: "bg-brand-solid text-white shadow-xs hover:bg-brand-solid_hover",
	destructive: "bg-error-solid text-white shadow-xs hover:bg-error-solid_hover",
	outline:
		"bg-primary text-secondary shadow-xs ring-1 ring-border-primary ring-inset hover:bg-primary_hover hover:text-secondary_hover",
	secondary:
		"bg-primary text-secondary shadow-xs ring-1 ring-border-primary ring-inset hover:bg-primary_hover hover:text-secondary_hover",
	ghost: "text-tertiary hover:bg-primary_hover hover:text-tertiary_hover",
	link: "text-brand-secondary underline-offset-4 hover:underline",
};

const sizeStyles: Record<ButtonSize, string> = {
	default: "h-9 px-4 py-2",
	sm: "h-8 px-3 text-xs",
	lg: "h-10 px-6",
	icon: "size-9",
};

export interface ButtonProps extends ComponentProps<"button"> {
	variant?: ButtonVariant;
	size?: ButtonSize;
	asChild?: boolean;
}

/**
 * Drop-in replacement for the shadcn Button, styled with Untitled UI button
 * tokens. Keeps the shadcn API (variant / size / asChild + native <button>
 * props incl. React 19 ref). `asChild` renders the single child element with
 * the button classes via cloneElement (no Radix Slot dependency).
 */
export function CustomButton({
	className,
	variant = "default",
	size = "default",
	asChild,
	children,
	type,
	ref,
	...props
}: ButtonProps) {
	const classes = cn(base, variantStyles[variant], sizeStyles[size], className);

	if (asChild && isValidElement(children)) {
		// biome-ignore lint/suspicious/noExplicitAny: clone an arbitrary child element
		const child = children as ReactElement<any>;
		return cloneElement(child, {
			className: cn(classes, child.props.className),
			ref,
			...props,
			...(type !== undefined ? { type } : {}),
		});
	}

	// Default to type="button" so a Button placed inside a <form> (e.g. a
	// dropdown trigger like MultiSelect) doesn't behave as an implicit submit
	// button. Real submit buttons opt in explicitly with type="submit".
	return (
		<button ref={ref} type={type ?? "button"} className={classes} {...props}>
			{children}
		</button>
	);
}

export { CustomButton as Button };
